'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import type {
  Tournament,
  Club,
  TournamentFormData,
} from '@/types/tournament.model';
import {
  TOURNAMENT_AGE_MAX_UNRESTRICTED,
  TOURNAMENT_AGE_MIN_UNRESTRICTED,
  TournamentFormat,
} from '@/types/tournament.model';
import { updateTournament } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';
import { SignupDeadlineField } from '@/features/tournament-wizard/blocks';
import type { ClubSchedulesSlice } from '@/features/tournament-wizard/engine/types';
import { resolveTournamentDatesFromClubSchedulesSlice } from '@/features/tournament-wizard/utils/clubScheduleDates';
import { hydrateClubSchedulesSliceFromTournament } from '@/features/tournament-wizard/utils/hydrateClubSchedulesFromTournament';
import { ClubSchedulesStepEmbed } from '@/components/tournament/details-page/edit-modals/ClubSchedulesStepEmbed';
import { formatDateForUser } from '@/utils/formatters';
import { toLocalMidnightDate } from '@/utils/localDateOnly';
import {
  latestSignupDeadlineDay,
  isSignupDeadlineTooLate,
} from '@/utils/signupDeadline';

interface EditDatesAndScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
  /** Called after a successful save (modal already PATCHed). Sync local state only — do not call updateTournament again. */
  onUpdate: (updatedTournament: Tournament) => void;
  /**
   * Only the schedule can be saved: the backend discards dates and clubs once the tournament has
   * registrations (`TournamentService.editTournamentPatch`). The modal then freezes the club list
   * and PATCHes `schedule` alone.
   *
   * The signup deadline is NOT one of those — it stays editable until the tournament has a fixture
   * — but it is shown read-only here anyway, because the dates it is validated against are frozen.
   * `EditSignupDeadlineModal` is its door in this state.
   */
  restrictedToSchedule?: boolean;
}

export function EditDatesAndScheduleModal({
  isOpen,
  onClose,
  tournament,
  onUpdate,
  restrictedToSchedule = false,
}: EditDatesAndScheduleModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [signupDeadlineError, setSignupDeadlineError] = useState<string | null>(
    null,
  );
  const [currentSchedule, setCurrentSchedule] = useState<Club | null>(null);
  const [clubSchedulesSlice, setClubSchedulesSlice] = useState<ClubSchedulesSlice>(
    () => ({
      clubSchedules: [],
      validForMatchDuration: Number(tournament.matchDuration),
    }),
  );
  const [formData, setFormData] = useState<TournamentFormData>({
    name: tournament.name,
    description: tournament.description,
    bannerUrl: tournament.bannerUrl || '',
    sport: tournament.sport,
    price: tournament.price,
    startDate: tournament.startDate ? new Date(tournament.startDate) : null,
    endDate: tournament.endDate ? new Date(tournament.endDate) : null,
    signupDeadlineDate: tournament.signupDeadlineDate
      ? new Date(tournament.signupDeadlineDate)
      : null,
    type: tournament.type,
    gender: tournament.gender,
    minCategory: tournament.minCategory,
    maxCategory: tournament.maxCategory,
    minTeamsAllowed: tournament.minTeamsAllowed,
    maxTeamsAllowed: tournament.maxTeamsAllowed,
    forceSum: tournament.forceSum,
    playersPerTeam: tournament.playersPerTeam,
    courts: [],
    clubs: tournament.clubs || [],
    schedule: [],
    prizes: tournament.prizes,
    paymentType: tournament.paymentType,
    teamsPerGroups: tournament.teamsPerGroups,
    classifiedPerGroups: tournament.classifiedPerGroups,
    fixtureType: tournament.fixtureType,
    format: tournament.format ?? TournamentFormat.SHORT,
    matchDuration: Number(tournament.matchDuration),
    inPause: tournament.inPause || false,
    minAge: tournament.minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED,
    maxAge: tournament.maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED,
  });

  const { toast } = useToast();

  const getEarliestScheduleDate = useCallback((): string | null => {
    if (formData.startDate) {
      return formData.startDate.toISOString().split('T')[0];
    }

    if (!currentSchedule?.schedules || currentSchedule.schedules.length === 0) {
      return null;
    }

    let earliestDate: string | null = null;
    currentSchedule.schedules.forEach((scheduleItem) => {
      if (scheduleItem.date) {
        if (!earliestDate || scheduleItem.date < earliestDate) {
          earliestDate = scheduleItem.date;
        }
      }
    });

    return earliestDate;
  }, [formData.startDate, currentSchedule]);

  const calendarMinDateSignup = useMemo(
    () => toLocalMidnightDate(new Date()) ?? undefined,
    [],
  );

  const calendarMaxDateSignup = useMemo(
    () => latestSignupDeadlineDay(getEarliestScheduleDate()) ?? undefined,
    [getEarliestScheduleDate],
  );

  const tournamentClubIds = useMemo(
    () =>
      restrictedToSchedule
        ? (tournament.clubs ?? []).map((club) => club.id)
        : undefined,
    [restrictedToSchedule, tournament.clubs],
  );

  const validateSignupDeadlineDate = useCallback(
    (deadline: Date | null): string | null => {
      if (!deadline) return null;

      const earliestScheduleDate = getEarliestScheduleDate();
      if (!earliestScheduleDate) return null;

      if (isSignupDeadlineTooLate(deadline, earliestScheduleDate)) {
        return `La fecha de cierre de inscripción debe ser al menos un día antes del inicio del torneo (${earliestScheduleDate})`;
      }

      return null;
    },
    [getEarliestScheduleDate],
  );

  const convertTournamentScheduleToClubSchedules = (
    t: Tournament,
  ): Club | null => {
    if (!t.clubs || t.clubs.length === 0) {
      return null;
    }

    const club = t.clubs[0];

    const chunks = t.schedule?.filter((s) => s.club.id === club.id) ?? [];

    if (chunks.length === 0) {
      return {
        ...club,
        schedules: [],
      };
    }

    const schedules = chunks.flatMap((tournamentSchedule) =>
      tournamentSchedule.schedule.map((daySchedule) => ({
        date: daySchedule.date,
        dayOfWeek: daySchedule.dayOfWeek,
        timeRange: daySchedule.timeRange.map((timeRange) => ({
          startTime: timeRange.startTime.substring(0, 5),
          endTime: timeRange.endTime.substring(0, 5),
          qty: timeRange.qty,
          courtsQuantity: timeRange.courtsQuantity,
        })),
      })),
    );

    return {
      ...club,
      schedules,
    };
  };

  useEffect(() => {
    if (!isOpen || !tournament) return;

    let cancelled = false;

    setStep(1);

    const clubWithSchedules =
      convertTournamentScheduleToClubSchedules(tournament);
    if (clubWithSchedules) {
      setCurrentSchedule(clubWithSchedules);
    }

    setClubSchedulesSlice({
      clubSchedules: [],
      validForMatchDuration: Number(tournament.matchDuration),
    });

    void (async () => {
      try {
        const hydrated = await hydrateClubSchedulesSliceFromTournament(tournament);
        if (!cancelled) {
          setClubSchedulesSlice(hydrated);
        }
      } catch (e) {
        console.error('hydrateClubSchedulesSliceFromTournament', e);
        if (!cancelled) {
          setClubSchedulesSlice({
            clubSchedules: [],
            validForMatchDuration: null,
          });
        }
      }
    })();

    setFormData({
      name: tournament.name,
      description: tournament.description,
      bannerUrl: tournament.bannerUrl || '',
      sport: tournament.sport,
      price: tournament.price,
      startDate: tournament.startDate ? new Date(tournament.startDate) : null,
      endDate: tournament.endDate ? new Date(tournament.endDate) : null,
      signupDeadlineDate: tournament.signupDeadlineDate
        ? new Date(tournament.signupDeadlineDate)
        : null,
      type: tournament.type,
      gender: tournament.gender,
      minCategory: tournament.minCategory,
      maxCategory: tournament.maxCategory,
      minTeamsAllowed: tournament.minTeamsAllowed,
      maxTeamsAllowed: tournament.maxTeamsAllowed,
      forceSum: tournament.forceSum,
      playersPerTeam: tournament.playersPerTeam,
      courts: [],
      clubs: tournament.clubs || [],
      schedule: [],
      prizes: tournament.prizes,
      paymentType: tournament.paymentType,
      teamsPerGroups: tournament.teamsPerGroups,
      classifiedPerGroups: tournament.classifiedPerGroups,
      fixtureType: tournament.fixtureType,
      format: tournament.format ?? TournamentFormat.SHORT,
      matchDuration: Number(tournament.matchDuration),
      inPause: tournament.inPause || false,
      minAge: tournament.minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED,
      maxAge: tournament.maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED,
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, tournament]);

  useEffect(() => {
    const err = validateSignupDeadlineDate(formData.signupDeadlineDate);
    setSignupDeadlineError(err);
  }, [
    formData.signupDeadlineDate,
    formData.startDate,
    formData.endDate,
    currentSchedule,
    validateSignupDeadlineDate,
  ]);

  const handleGoToStep2 = () => {
    const err = validateSignupDeadlineDate(formData.signupDeadlineDate);
    if (err) {
      toast({
        title: 'Error de validación',
        description: err,
        variant: 'destructive',
      });
      return;
    }
    setStep(2);
  };

  const handleSave = async () => {
    if (signupDeadlineError) {
      toast({
        title: 'Error de validación',
        description: signupDeadlineError,
        variant: 'destructive',
      });
      return;
    }

    // A LONG's weekly grid can't be changed after creation (the backend ignores `schedule` for it),
    // so there is no schedule to pick: only the signup deadline is saved.
    const isLong = tournament.format === TournamentFormat.LONG;

    const hasScheduleSelection = clubSchedulesSlice.clubSchedules.some(
      (cs) => cs.scheduleIds.length > 0,
    );
    if (!isLong && !hasScheduleSelection) {
      toast({
        title: 'Seleccioná horarios',
        description:
          'Elegí al menos un horario reutilizable para guardar los cambios.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      if (isLong) {
        const signupDeadline = formData.signupDeadlineDate
          ? new Date(formData.signupDeadlineDate)
          : null;
        signupDeadline?.setHours(0, 0, 0, 0);
        const updatedLong = await updateTournament(tournament.id, {
          signupDeadlineDate: signupDeadline,
        });
        onUpdate(updatedLong);
        handleClose();
        toast({
          title: 'Torneo actualizado',
          description: 'El cierre de inscripción fue actualizado correctamente',
        });
        return;
      }

      const dates = await resolveTournamentDatesFromClubSchedulesSlice(
        clubSchedulesSlice,
        formData.signupDeadlineDate,
      );

      const startDateForBackend = new Date(dates.startDate);
      startDateForBackend.setHours(0, 0, 0, 0);

      const endDateForBackend = new Date(dates.endDate);
      endDateForBackend.setHours(0, 0, 0, 0);

      // Re-checked against the dates the schedule actually resolved to, which can differ from the
      // ones the field was validated against while the modal was open.
      const signupDeadlineForBackend = formData.signupDeadlineDate
        ? new Date(formData.signupDeadlineDate)
        : null;
      if (
        signupDeadlineForBackend &&
        isSignupDeadlineTooLate(signupDeadlineForBackend, dates.startDate)
      ) {
        toast({
          title: 'Error de validación',
          description: `La fecha de cierre de inscripción debe ser al menos un día antes del inicio del torneo (${startDateForBackend.toISOString().split('T')[0]})`,
          variant: 'destructive',
        });
        return;
      }

      if (restrictedToSchedule) {
        const tournamentStart = toLocalMidnightDate(tournament.startDate);
        const tournamentEnd = toLocalMidnightDate(tournament.endDate);

        if (
          (tournamentStart &&
            startDateForBackend.getTime() < tournamentStart.getTime()) ||
          (tournamentEnd &&
            endDateForBackend.getTime() > tournamentEnd.getTime())
        ) {
          toast({
            title: 'Horarios fuera de las fechas del torneo',
            description: `Los bloques elegidos incluyen días fuera del rango del torneo (${formatDateForUser(
              tournament.startDate,
            )} a ${formatDateForUser(tournament.endDate)}). Con parejas inscriptas las fechas no se pueden cambiar, así que elegí bloques dentro de ese rango.`,
            variant: 'destructive',
          });
          return;
        }
      }

      const clubScheduleData = clubSchedulesSlice.clubSchedules;
      const clubs = clubScheduleData.map((cs) => cs.club);
      const allScheduleIds = clubScheduleData.flatMap((cs) => cs.scheduleIds);
      const schedule = allScheduleIds.map((id) => ({ id }));

      // With registrations the backend applies `schedule` and silently drops the rest,
      // so sending them would only make the modal look like it saved more than it did.
      const updatePayload = restrictedToSchedule
        ? { schedule }
        : {
            startDate: startDateForBackend,
            endDate: endDateForBackend,
            signupDeadlineDate: signupDeadlineForBackend,
            schedule,
            clubs,
          };

      const updatedTournament = await updateTournament(
        tournament.id,
        updatePayload,
      );

      onUpdate(updatedTournament);
      handleClose();

      toast({
        title: 'Torneo actualizado',
        description: restrictedToSchedule
          ? 'Los horarios han sido actualizados correctamente'
          : 'Las fechas y horarios han sido actualizados correctamente',
      });
    } catch (error) {
      console.error('Error updating tournament:', error);
      handleGlobalError(error, 'Error', 'No se pudo actualizar el torneo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setCurrentSchedule(null);
    setClubSchedulesSlice({
      clubSchedules: [],
      validForMatchDuration: Number(tournament.matchDuration),
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {restrictedToSchedule
              ? step === 1
                ? 'Horarios (1/2) — Cierre de inscripción'
                : 'Horarios (2/2) — Bloques por club'
              : step === 1
                ? 'Fechas y horarios (1/2) — Cierre de inscripción'
                : 'Fechas y horarios (2/2) — Horarios'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {step === 1
              ? 'Elegí la fecha límite de inscripción. Luego podrás editar horarios.'
              : 'Elegí los horarios reutilizables por club.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <SignupDeadlineField
              value={formData.signupDeadlineDate}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, signupDeadlineDate: date }))
              }
              calendarMinDate={calendarMinDateSignup}
              calendarMaxDate={calendarMaxDateSignup}
              wrapInCard={false}
              inputId="edit-tournament-signup-deadline"
              disabled={restrictedToSchedule}
            />
            {restrictedToSchedule && (
              <p className="text-sm text-amber-600">
                Acá no se puede tocar el cierre porque las fechas del torneo ya
                están fijas. Editalo desde el lápiz de «Inscripciones hasta».
              </p>
            )}
            {signupDeadlineError && (
              <p className="text-sm text-red-500">{signupDeadlineError}</p>
            )}
            {getEarliestScheduleDate() && !signupDeadlineError && (
              <p className="text-xs text-muted-foreground">
                Debe ser al menos un día antes del inicio del torneo (
                {getEarliestScheduleDate()})
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <ClubSchedulesStepEmbed
              key={`club-schedules-${isOpen}`}
              tournament={tournament}
              formData={formData}
              value={clubSchedulesSlice}
              onChange={setClubSchedulesSlice}
              allowedClubIds={tournamentClubIds}
            />
          </div>
        )}

        <div className="flex justify-between border-t pt-4">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleGoToStep2}>
                Siguiente
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                Atrás
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                {isLoading ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

